import React from "react";
import { Select, Skeleton, Radio, Switch } from "antd";
import { MarketOption, SectorOption } from "./Heatmap";

const { Option } = Select;

const ALL_SECTORS = "all";

const labelStyle: React.CSSProperties = {
  fontSize: "11px",
  fontFamily: "'DM Mono', monospace",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  marginBottom: "4px",
  display: "block",
  fontWeight: 600,
};

const LEGEND_ITEMS = [
  { label: "≥+5%", bg: "#0d5c2e" },
  { label: "+3%", bg: "#166634" },
  { label: "+1.5%", bg: "#15803d" },
  { label: "+0.5%", bg: "#16a34a" },
  { label: "0%", bg: "#374151" },
  { label: "-0.5%", bg: "#b91c1c" },
  { label: "-1.5%", bg: "#991b1b" },
  { label: "-3%", bg: "#7f1d1d" },
  { label: "≤-10%", bg: "#450a0a" },
];

interface HeatmapFiltersProps {
  markets?: MarketOption[];
  marketsLoading?: boolean;
  selectedMarket?: number | string;
  onMarketChange?: (value: string | number) => void;
  sectors?: SectorOption[];
  stocksLoading?: boolean;
  selectedSector?: string;
  onSectorChange?: (value: string) => void;
  selectLabelColor: string;
  dropdownClass: string;
  legendText: string;
  sortBy: "total_trades" | "market_cap";
  onSortChange: (value: "total_trades" | "market_cap") => void;
  groupBySector: boolean;
  onGroupBySectorChange: (value: boolean) => void;
  hideLogo: boolean;
  onHideLogoChange: (value: boolean) => void;
}

export const HeatmapFilters: React.FC<HeatmapFiltersProps> = ({
  markets,
  marketsLoading,
  selectedMarket,
  onMarketChange,
  sectors,
  stocksLoading,
  selectedSector,
  onSectorChange,
  selectLabelColor,
  dropdownClass,
  legendText,
  sortBy,
  onSortChange,
  groupBySector,
  onGroupBySectorChange,
  hideLogo,
  onHideLogoChange,
}) => {
  const showFilters =
    marketsLoading ||
    (markets && markets.length > 0) ||
    (!stocksLoading && sectors && sectors.length > 0);

  if (!showFilters) return null;

  const showSectorDropdown =
    !groupBySector &&
    !stocksLoading &&
    sectors &&
    sectors.length > 0 &&
    onSectorChange;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-end",
        gap: "12px",
        marginBottom: "14px",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          paddingBottom: "6px",
        }}
      >
        <label style={{ ...labelStyle, color: selectLabelColor }}>
          Group by
        </label>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            paddingTop: "4px",
          }}
        >
          <Switch
            checked={groupBySector}
            onChange={onGroupBySectorChange}
            size="small"
          />
          <span
            style={{
              fontSize: "11px",
              fontFamily: "'DM Mono', monospace",
              color: selectLabelColor,
              opacity: 0.75,
            }}
          >
            {groupBySector ? "Sector" : "Sector"}
          </span>
        </div>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          paddingBottom: "6px",
        }}
      >
        <label style={{ ...labelStyle, color: selectLabelColor }}>Logos</label>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            paddingTop: "4px",
          }}
        >
          <Switch checked={hideLogo} onChange={onHideLogoChange} size="small" />
          <span
            style={{
              fontSize: "11px",
              fontFamily: "'DM Mono', monospace",
              color: selectLabelColor,
              opacity: 0.75,
            }}
          >
            {hideLogo ? "Hidden" : "Visible"}
          </span>
        </div>
      </div>

      {marketsLoading ? (
        <div style={{ width: 200 }}>
          <Skeleton.Input active style={{ width: 200, height: 32 }} />
        </div>
      ) : markets && markets.length > 0 && onMarketChange ? (
        <div style={{ width: 200 }}>
          <label style={{ ...labelStyle, color: selectLabelColor }}>
            Market
          </label>
          <Select
            value={selectedMarket}
            onChange={onMarketChange}
            style={{ width: "100%" }}
            size="middle"
            popupClassName={dropdownClass}
          >
            {markets.map((m) => (
              <Option key={m.id} value={m.id}>
                {m.market_name}
              </Option>
            ))}
          </Select>
        </div>
      ) : null}

      {showSectorDropdown ? (
        <div style={{ width: 200 }}>
          <label style={{ ...labelStyle, color: selectLabelColor }}>
            Sector
          </label>
          <Select
            value={selectedSector}
            onChange={onSectorChange}
            style={{ width: "100%" }}
            size="middle"
            popupClassName={dropdownClass}
          >
            <Option value={ALL_SECTORS}>All Sectors</Option>
            {sectors!.map(({ id, name }) => (
              <Option key={id} value={String(id)}>
                {name}
              </Option>
            ))}
          </Select>
        </div>
      ) : null}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          paddingBottom: "6px",
        }}
      >
        <label style={{ ...labelStyle, color: selectLabelColor }}>
          Sort By
        </label>
        <Radio.Group
          value={sortBy}
          onChange={(e) => onSortChange(e.target.value)}
          size="small"
        >
          <Radio value="total_trades" style={{ color: selectLabelColor }}>
            No. of Trades
          </Radio>
          <Radio value="market_cap" style={{ color: selectLabelColor }}>
            Market Cap
          </Radio>
        </Radio.Group>
      </div>

      {/* Legend */}
      <div
        style={{
          display: "flex",
          gap: "4px",
          alignItems: "center",
          flexWrap: "wrap",
          paddingBottom: "8px",
        }}
      >
        {LEGEND_ITEMS.map(({ label, bg }) => (
          <div
            key={label}
            style={{
              background: bg,
              borderRadius: "3px",
              padding: "3px 7px",
              fontSize: "9px",
              color: legendText,
              fontFamily: "'DM Mono', monospace",
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </div>
        ))}
      </div>
    </div>
  );
};
